import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { MapPin, ArrowLeft, Search, ChevronRight, Home } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import ListingsGrid from "@/components/ListingsGrid";
import Footer from "@/components/Footer";
import WhatsAppFloatingButton from "@/components/WhatsAppFloatingButton";
import ResponsiveImage from "@/components/ResponsiveImage";
import FaqSection from "@/components/FaqSection";
import PropertySearchSection from "@/components/PropertySearchSection";
import ScheduleVisitDialog from "@/components/ScheduleVisitDialog";
import LeadQualificationSection from "@/components/LeadQualificationSection";
import { THANK_YOU } from "@/lib/thankYou";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { fetchListings, type ListingFilters } from "@/lib/listings";
import { Star } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import { buildNeighborhoodGraph } from "@/lib/schemaOrg";

interface FeaturedLandingPage {
  title: string;
  description: string;
  price: string;
  route: string;
  image?: string;
}

interface FeaturedLandingPage {
  title: string;
  description: string;
  price: string;
  route: string;
  image?: string;
}

type PictureImport = {
  sources: Record<string, string>;
  img: { src: string; w: number; h: number };
};

interface NeighborhoodPageProps {
  name: string;
  slug: string;
  description: string;
  heroImage?: string;
  heroPicture?: PictureImport;
  featuredPages?: FeaturedLandingPage[];
}

const NeighborhoodPage = ({ name, slug, description, heroImage, heroPicture, featuredPages }: NeighborhoodPageProps) => {
  const { t } = useLanguage();
  const [propertyType, setPropertyType] = useState("");
  const [bedroomsMin, setBedroomsMin] = useState("");
  const [garageMin, setGarageMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [areaMin, setAreaMin] = useState("");
  const [filters, setFilters] = useState<ListingFilters>({ neighborhood: name });

  const pageTitle = `Imóveis à venda em ${name} | TopClass Imóveis`;
  const pageDescription = `${name} - ${description}`;

  usePageMeta({
    title: pageTitle,
    description: pageDescription,
    keywords: `Imóveis à venda em ${name}, Apartamentos ${name}, Coberturas ${name}, Casas ${name}, ${name} Rio de Janeiro, Topclass Imóveis ${name}`,
    ogTitle: pageTitle,
    ogDescription: pageDescription,
  });

  useEffect(() => {
    const graph = buildNeighborhoodGraph(name, `/${slug}`);
    const placeNode: any = graph["@graph"][0];
    placeNode.name = `Imóveis em ${name}`;
    if (description) placeNode.description = description;

    document.getElementById("ld-neighborhood")?.remove();
    const s1 = document.createElement("script");
    s1.type = "application/ld+json";
    s1.id = "ld-neighborhood";
    s1.text = JSON.stringify(graph);
    document.head.appendChild(s1);
    return () => {
      document.getElementById("ld-neighborhood")?.remove();
    };
  }, [name, slug, description]);


  const { data: listings = [], isLoading } = useQuery({
    queryKey: ["listings", slug, filters],
    queryFn: () => fetchListings(filters),
  });

  const handleSearch = () => {
    setFilters({
      neighborhood: name,
      property_type: propertyType || undefined,
      bedrooms_min: bedroomsMin ? Number(bedroomsMin) : undefined,
      garage_min: garageMin ? Number(garageMin) : undefined,
      price_max: priceMax ? Number(priceMax) : undefined,
      area_min: areaMin ? Number(areaMin) : undefined,
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16">
        <nav aria-label="Breadcrumb" className="container mx-auto px-4 py-3">
          <ol className="flex items-center gap-1.5 text-sm font-body text-muted-foreground flex-wrap">
            <li>
              <Link to="/" title="Página inicial da TopClass Imóveis" className="inline-flex items-center gap-1 hover:text-accent transition-colors">
                <Home className="h-3.5 w-3.5" />
                <span>Home</span>
              </Link>
            </li>
            <li aria-hidden="true"><ChevronRight className="h-3.5 w-3.5" /></li>
            <li>
              <span className="text-foreground font-semibold" aria-current="page">{name}</span>
            </li>
          </ol>
        </nav>
        <section className="relative bg-navy py-16 md:py-0 md:min-h-[85vh] md:flex md:items-center overflow-hidden">
          {heroPicture ? (
            <ResponsiveImage
              picture={heroPicture}
              alt={`Vista de ${name}`}
              className="absolute inset-0 w-full h-full object-cover opacity-50 md:opacity-100"
              sizes="100vw"
              eager
              fetchPriority="high"
            />
          ) : heroImage ? (
            <img
              src={heroImage}
              alt={`Vista de ${name}`}
              className="absolute inset-0 w-full h-full object-cover opacity-50 md:opacity-100"
              width={1920}
              height={1080}
              loading="eager"
              decoding="async"
            />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-navy/90 md:bg-gradient-to-r md:from-black/80 md:via-black/50 md:to-transparent" />

          <div className="relative container mx-auto px-4 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-4">
              <MapPin className="h-6 w-6 text-accent" />
              <span className="text-accent font-body text-sm uppercase tracking-widest">
                {t("neighborhood.premiumRegion")}
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold text-white mb-4 md:max-w-3xl">
              {name}
            </h1>
            <p className="text-lg text-white/90 font-body max-w-2xl mx-auto md:mx-0 mb-6">
              {description}
            </p>
            <Link to="/" title="Página inicial da TopClass Imóveis">
              <Button variant="gold" size="sm">
                <ArrowLeft className="h-4 w-4 mr-1" />
                {t("neighborhood.backHome")}
              </Button>
            </Link>
          </div>
        </section>

        <section className="relative -mt-8 z-20 container mx-auto px-4">
          <div className="bg-card rounded-lg shadow-2xl p-6 md:p-8 border border-border">
            <h2 className="text-xl font-display font-bold text-foreground mb-4 text-center">
              {t("neighborhood.filterTitle")} {name}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <Select value={propertyType} onValueChange={setPropertyType}>
                <SelectTrigger>
                  <SelectValue placeholder={t("search.type")} />
                </SelectTrigger>
                <SelectContent>
                  {["apartamento", "cobertura", "casa"].map((v) => (
                    <SelectItem key={v} value={v}>{t(`type.${v}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={bedroomsMin} onValueChange={setBedroomsMin}>
                <SelectTrigger>
                  <SelectValue placeholder={t("search.bedrooms")} />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}+ {t("neighborhood.bedroomsPlus")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={garageMin} onValueChange={setGarageMin}>
                <SelectTrigger>
                  <SelectValue placeholder={t("search.garage")} />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4].map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}+ {t("neighborhood.spotsPlus")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                type="number"
                placeholder={t("neighborhood.areaMin")}
                value={areaMin}
                onChange={(e) => setAreaMin(e.target.value)}
                className="font-body"
              />

              <Input
                type="number"
                placeholder={t("neighborhood.priceMax")}
                value={priceMax}
                onChange={(e) => setPriceMax(e.target.value)}
                className="font-body"
              />
            </div>
            <div className="mt-4 flex justify-center">
              <Button variant="gold" size="lg" onClick={handleSearch} className="min-w-[200px]">
                <Search className="h-5 w-5" />
                {t("neighborhood.searchButton")}
              </Button>
            </div>
          </div>
        </section>

        {featuredPages && featuredPages.length > 0 && (
          <section className="container mx-auto px-4 py-12">
            <div className="flex items-center gap-2 mb-6">
              <Star className="h-5 w-5 text-accent fill-accent" />
              <h2 className="text-2xl font-display font-bold text-foreground">
                {t("neighborhood.highlightsIn")} {name}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredPages.map((page) => (
                <Link key={page.route} to={page.route} title={page.title ?? page.route} className="group">
                  <div className="bg-card rounded-lg border border-border overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-1">
                    {page.image && (
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={page.image}
                          alt={page.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      <span className="text-xs uppercase tracking-widest text-accent font-body font-semibold">
                        {t("neighborhood.exclusiveOpportunity")}
                      </span>
                      <h3 className="text-lg font-display font-bold text-foreground mt-1 mb-2">
                        {page.title}
                      </h3>
                      <p className="text-sm text-muted-foreground font-body mb-3">
                        {page.description}
                      </p>
                      <p className="text-xl font-display font-bold text-accent">
                        {page.price}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <ListingsGrid listings={listings} isLoading={isLoading} />

        <LeadQualificationSection defaultNeighborhood={name} thankYouPath={THANK_YOU.bairros} />

        {/* CTA de agendamento */}
        <section className="py-12 bg-background border-y border-border">
          <div className="container mx-auto px-4 text-center">
            <ScheduleVisitDialog
              propertyName={`Imóveis em ${name}`}
              neighborhood={name}
              label="Quero conhecer este imóvel"
              className="text-lg px-10 py-6"
            />
          </div>
        </section>

        <PropertySearchSection initialFilters={{}} />

        <FaqSection
          scriptId={`bairro-${slug}`}
          title={`Perguntas Frequentes sobre imóveis em ${name}`}
          items={[
            {
              question: `Quais tipos de imóveis estão à venda em ${name}?`,
              answer: `Em ${name} oferecemos apartamentos de 1 a 5 quartos, coberturas duplex, studios e casas de alto padrão. Todos com curadoria da TopClass Imóveis.`,
            },
            {
              question: `Qual a faixa de preço dos imóveis em ${name}?`,
              answer: `Os valores em ${name} variam conforme metragem, andar, vista e estado do imóvel. Use os filtros de busca acima para encontrar opções dentro do seu orçamento.`,
            },
            {
              question: `${name} é uma boa região para investir?`,
              answer: `Sim. ${description} Isso torna a região atrativa tanto para moradia quanto para investimento (locação anual, temporada e Airbnb, quando permitido).`,
            },
            {
              question: `Como agendar uma visita a um imóvel em ${name}?`,
              answer: `Escolha o imóvel de interesse, clique em "Ver detalhes" e agende diretamente pelo WhatsApp (21) 97354-8387 ou pelo formulário da página. Atendemos em português e inglês.`,
            },
            {
              question: `Vocês trabalham com financiamento e documentação em ${name}?`,
              answer: `Sim. Assessoramos toda a compra: análise documental, simulação de financiamento, ITBI, registro em cartório e entrega das chaves.`,
            },
          ]}
        />
      </main>
      <Footer />
      <WhatsAppFloatingButton />
    </div>
  );
};

export default NeighborhoodPage;